import type {
  ArtistAvailabilityAdjustment,
  ArtistWeekReconciliationReasonCode,
  ArtistWeekReconciliationStatus,
  ArtistWeekReconciliationSummary,
  ArtistWeekReconciliationTaskSummary,
  FinanceDataReliability,
  FinanceReconciliationAlert,
  FinanceReconciliationPeriodSummary,
  FinanceReconciliationReport,
  FinanceReconciliationThresholds,
  Person,
  Task,
  TimeLog,
} from "../models/production";

const DEFAULT_THRESHOLDS: FinanceReconciliationThresholds = {
  minutesPerDay: 480,
  targetWorkingDaysPerWeek: 5,
  healthyTimelogRatio: 0.9,
  healthyUtilisationRatio: 0.9,
  overloadedUtilisationRatio: 1.1,
  problemUnaccountedDays: 1,
  lowConfidenceTimelogRatio: 0.75,
};

interface FinanceReconciliationContext {
  tasks?: Task[];
  timeLogs?: TimeLog[];
  availabilityAdjustments?: ArtistAvailabilityAdjustment[];
  thresholds?: Partial<FinanceReconciliationThresholds>;
  now?: string;
}

interface BuildFinanceReconciliationReportArgs extends FinanceReconciliationContext {
  people: Array<Pick<Person, "id" | "displayName" | "fullName" | "department" | "role">>;
  startDate: string;
  endDate: string;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function roundDays(value: number) {
  return Math.round(value * 100) / 100;
}

function toIsoDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

function startOfIsoWeek(dateLike: string | Date) {
  const date = new Date(dateLike);
  const utcDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = utcDate.getUTCDay() || 7;
  utcDate.setUTCDate(utcDate.getUTCDate() - day + 1);
  return utcDate;
}

function addUtcDays(date: Date, days: number) {
  const next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function listIsoWeeksBetween(startDate: string, endDate: string) {
  const start = startOfIsoWeek(startDate);
  const end = startOfIsoWeek(endDate);
  const weeks: string[] = [];
  let cursor = new Date(start.getTime());
  while (cursor <= end) {
    weeks.push(toIsoDate(cursor));
    cursor = addUtcDays(cursor, 7);
  }
  return weeks;
}

function countWorkingDaysForWeek(weekStartIso: string, unavailableDates: Set<string>, approvedLeaveDays: number) {
  let workingDays = 0;
  let leaveCount = 0;
  for (let i = 0; i < 7; i += 1) {
    const day = addUtcDays(startOfIsoWeek(weekStartIso), i);
    const iso = toIsoDate(day);
    const weekday = day.getUTCDay() || 7;
    if (weekday >= 1 && weekday <= 5) {
      if (unavailableDates.has(iso)) {
        leaveCount += 1;
      } else {
        workingDays += 1;
      }
    }
  }
  const extraLeave = Math.max(0, approvedLeaveDays - leaveCount);
  return Math.max(0, workingDays - extraLeave);
}

function getAvailabilityForWeek(personId: string, weekStartIso: string, adjustments: ArtistAvailabilityAdjustment[] = []) {
  const adjustment = adjustments.find((item) => item.personId === personId && item.weekStart === weekStartIso);
  const unavailableDates = new Set(adjustment?.unavailableDates || []);
  return {
    unavailableDates,
    approvedLeaveDays: adjustment?.unavailableDays ?? adjustment?.unavailableDates?.length ?? 0,
  };
}

function countWorkingDaysBetween(startIso?: string, endIso?: string) {
  if (!startIso || !endIso) return 0;
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end < start) return 0;
  let total = 0;
  let cursor = new Date(start.getTime());
  cursor.setUTCHours(0, 0, 0, 0);
  end.setUTCHours(0, 0, 0, 0);
  while (cursor <= end) {
    const weekday = cursor.getUTCDay() || 7;
    if (weekday >= 1 && weekday <= 5) total += 1;
    cursor = addUtcDays(cursor, 1);
  }
  return total;
}

function getTaskWeekAllocation(task: Task, weekStart: string, weekEnd: string, minutesPerDay: number) {
  const bidDays = typeof task.bidHours === "number" && Number.isFinite(task.bidHours) ? task.bidHours : 0;
  if (bidDays <= 0) {
    return { allocatedMinutes: 0, usedScheduleAssumption: false };
  }

  const scheduleStart = task.plannedStartDate || task.createdAt?.slice(0, 10);
  const scheduleEnd = task.plannedEndDate || task.dueDate || task.plannedStartDate || task.updatedAt?.slice(0, 10) || scheduleStart;

  if (!scheduleStart || !scheduleEnd) {
    return { allocatedMinutes: bidDays * minutesPerDay, usedScheduleAssumption: true };
  }

  const taskWorkingDays = countWorkingDaysBetween(scheduleStart, scheduleEnd);
  const overlapStart = scheduleStart > weekStart ? scheduleStart : weekStart;
  const overlapEnd = scheduleEnd < weekEnd ? scheduleEnd : weekEnd;
  const overlapWorkingDays = countWorkingDaysBetween(overlapStart, overlapEnd);

  if (taskWorkingDays <= 0) {
    return { allocatedMinutes: bidDays * minutesPerDay, usedScheduleAssumption: true };
  }

  return {
    allocatedMinutes: (bidDays * overlapWorkingDays * minutesPerDay) / taskWorkingDays,
    usedScheduleAssumption: false,
  };
}

function getArtistName(person: Pick<Person, "displayName" | "fullName" | "id">) {
  return person.displayName || person.fullName || person.id;
}

function reliabilityFromSignals(args: {
  reasonCodes: ArtistWeekReconciliationReasonCode[];
  timelogRatio: number;
  utilisationRatio: number;
  usedScheduleAssumptions: boolean;
}) {
  let score = 100;
  if (args.usedScheduleAssumptions) score -= 10;
  if (args.reasonCodes.includes("no_assignment_data")) score -= 25;
  if (args.reasonCodes.includes("missing_timelogs")) score -= 35;
  if (args.reasonCodes.includes("partial_timelogs")) score -= 20;
  if (args.reasonCodes.includes("low_confidence_week")) score -= 20;
  if (args.reasonCodes.includes("possible_non_task_time")) score -= 10;
  if (args.timelogRatio < 0.5) score -= 10;
  if (args.utilisationRatio < 0.5) score -= 10;
  if (score >= 80) return "high" satisfies FinanceDataReliability;
  if (score >= 55) return "medium" satisfies FinanceDataReliability;
  return "low" satisfies FinanceDataReliability;
}

function deriveStatus(args: {
  timelogRatio: number;
  utilisationRatio: number;
  expectedMinutes: number;
  loggedMinutes: number;
  allocatedMinutes: number;
  thresholds: FinanceReconciliationThresholds;
}) {
  const { timelogRatio, utilisationRatio, expectedMinutes, loggedMinutes, allocatedMinutes, thresholds } = args;
  const loggingGapMinutes = Math.max(0, allocatedMinutes - loggedMinutes);
  const allocationGapMinutes = Math.max(0, expectedMinutes - allocatedMinutes);
  const allocationVsLoggingVarianceMinutes = Math.abs(allocatedMinutes - loggedMinutes);
  const varianceToleranceMinutes = thresholds.minutesPerDay * 0.25;

  if (utilisationRatio >= thresholds.overloadedUtilisationRatio && timelogRatio >= thresholds.healthyTimelogRatio) {
    return "overloaded" satisfies ArtistWeekReconciliationStatus;
  }
  if (utilisationRatio >= thresholds.healthyUtilisationRatio && timelogRatio < thresholds.healthyTimelogRatio) {
    return "under_logging" satisfies ArtistWeekReconciliationStatus;
  }
  if (
    utilisationRatio < thresholds.healthyUtilisationRatio &&
    allocatedMinutes > 0 &&
    allocationVsLoggingVarianceMinutes <= varianceToleranceMinutes
  ) {
    return "under_utilised" satisfies ArtistWeekReconciliationStatus;
  }
  if (utilisationRatio < thresholds.healthyUtilisationRatio && loggingGapMinutes > thresholds.minutesPerDay * 0.5 && allocationGapMinutes > thresholds.minutesPerDay * 0.5) {
    return "mixed_review" satisfies ArtistWeekReconciliationStatus;
  }
  if (utilisationRatio < thresholds.healthyUtilisationRatio && timelogRatio < thresholds.healthyTimelogRatio) {
    return "mixed_review" satisfies ArtistWeekReconciliationStatus;
  }
  return "healthy" satisfies ArtistWeekReconciliationStatus;
}

function buildNotes(status: ArtistWeekReconciliationStatus, reasonCodes: ArtistWeekReconciliationReasonCode[]) {
  const notes: string[] = [];
  if (status === "under_logging") {
    notes.push("Allocation looked healthy, but logged time fell short of expected paid time.");
  }
  if (status === "under_utilised") {
    notes.push("Logged time broadly follows allocation, but not enough work appears to have been allocated for a full week.");
  }
  if (status === "mixed_review") {
    notes.push("This week shows both an allocation gap and a logging gap, so it needs manager review.");
  }
  if (status === "overloaded") {
    notes.push("Allocated work exceeded normal weekly capacity, which can hide schedule or staffing pressure.");
  }
  if (reasonCodes.includes("possible_non_task_time")) {
    notes.push("Some logged time may sit outside normal task allocation patterns.");
  }
  if (notes.length === 0) {
    notes.push("Logged time and allocation are broadly aligned for this week.");
  }
  return notes;
}

export function computeArtistWeekReconciliationSummary(
  person: Pick<Person, "id" | "displayName" | "fullName" | "department" | "role">,
  weekStartIso: string,
  context: FinanceReconciliationContext = {}
): ArtistWeekReconciliationSummary {
  const thresholds = { ...DEFAULT_THRESHOLDS, ...(context.thresholds || {}) };
  const weekStart = toIsoDate(startOfIsoWeek(weekStartIso));
  const weekEnd = toIsoDate(addUtcDays(startOfIsoWeek(weekStartIso), 6));
  const availability = getAvailabilityForWeek(person.id, weekStart, context.availabilityAdjustments);
  const expectedDays = countWorkingDaysForWeek(weekStart, availability.unavailableDates, availability.approvedLeaveDays);
  const expectedMinutes = expectedDays * thresholds.minutesPerDay;

  const personLogs = (context.timeLogs || []).filter(
    (timeLog) => timeLog.personId === person.id && timeLog.logDate >= weekStart && timeLog.logDate <= weekEnd
  );
  const loggedMinutes = personLogs.reduce((sum, timeLog) => sum + (timeLog.minutes || 0), 0);
  const taskMap = new Map((context.tasks || []).map((task) => [task.id, task]));
  const assignedTasks = (context.tasks || []).filter((task) => (task.assignedArtistIds || []).includes(person.id));

  let allocatedMinutes = 0;
  let usedScheduleAssumptions = false;
  const taskAllocatedMinutes = new Map<string, number>();
  for (const task of assignedTasks) {
    const allocation = getTaskWeekAllocation(task, weekStart, weekEnd, thresholds.minutesPerDay);
    allocatedMinutes += allocation.allocatedMinutes;
    usedScheduleAssumptions = usedScheduleAssumptions || allocation.usedScheduleAssumption;
    taskAllocatedMinutes.set(task.id, allocation.allocatedMinutes);
  }

  const reasonCodes = new Set<ArtistWeekReconciliationReasonCode>();
  const timelogRatio = expectedMinutes > 0 ? clamp(loggedMinutes / expectedMinutes, 0, 1.5) : 1;
  const utilisationRatio = expectedMinutes > 0 ? clamp(allocatedMinutes / expectedMinutes, 0, 1.5) : 1;
  const unaccountedMinutes = Math.max(0, expectedMinutes - loggedMinutes);

  if (loggedMinutes === 0) {
    reasonCodes.add("missing_timelogs");
  } else if (timelogRatio < thresholds.healthyTimelogRatio) {
    reasonCodes.add("partial_timelogs");
  }
  if (allocatedMinutes === 0) {
    reasonCodes.add("no_assignment_data");
  } else if (utilisationRatio < thresholds.healthyUtilisationRatio) {
    reasonCodes.add("under_allocated");
  } else if (utilisationRatio > thresholds.overloadedUtilisationRatio) {
    reasonCodes.add("over_allocated");
  }
  if (timelogRatio < thresholds.lowConfidenceTimelogRatio || unaccountedMinutes >= thresholds.problemUnaccountedDays * thresholds.minutesPerDay) {
    reasonCodes.add("low_confidence_week");
  }
  if (allocatedMinutes > 0 && loggedMinutes > allocatedMinutes + thresholds.minutesPerDay * 0.5) {
    reasonCodes.add("possible_non_task_time");
  }

  const status = deriveStatus({
    timelogRatio,
    utilisationRatio,
    expectedMinutes,
    loggedMinutes,
    allocatedMinutes,
    thresholds,
  });

  const recoveredMissingMinutes =
    status === "under_logging" || status === "mixed_review"
      ? Math.max(0, Math.min(expectedMinutes, Math.max(allocatedMinutes, loggedMinutes)) - loggedMinutes)
      : 0;

  const taskSummaryMap = personLogs.reduce<Map<string, ArtistWeekReconciliationTaskSummary>>((map, timeLog) => {
      const task = taskMap.get(String(timeLog.taskId || ""));
      const key = String(timeLog.taskId || timeLog.taskFptId || `unlinked:${timeLog.id}`);
      const existing = map.get(key) || {
        taskId: task?.id || timeLog.taskId,
        taskTitle: task?.title || timeLog.taskId || timeLog.taskFptId || "Unlinked task",
        parentLabel: task ? `${task.parentEntityType} ${task.parentEntityId}` : undefined,
        department: task?.taskType || task?.pipelineStep || undefined,
        loggedMinutes: 0,
        loggedDays: 0,
        allocatedMinutes: task?.id ? taskAllocatedMinutes.get(task.id) : undefined,
        allocatedDays: task?.id ? roundDays((taskAllocatedMinutes.get(task.id) || 0) / thresholds.minutesPerDay) : undefined,
      };
      existing.loggedMinutes += timeLog.minutes || 0;
      existing.loggedDays = roundDays(existing.loggedMinutes / thresholds.minutesPerDay);
      map.set(key, existing);
      return map;
    }, new Map());
  const taskSummaries = Array.from(taskSummaryMap.values()).sort((a, b) => b.loggedMinutes - a.loggedMinutes);

  const departments = Array.from(
    new Set(
      [
        ...assignedTasks.map((task) => task.taskType || task.pipelineStep || "").filter(Boolean),
        ...taskSummaries.map((task) => task.department || "").filter(Boolean),
        person.department || "",
      ].filter(Boolean)
    )
  );

  const confidence = reliabilityFromSignals({
    reasonCodes: Array.from(reasonCodes),
    timelogRatio,
    utilisationRatio,
    usedScheduleAssumptions,
  });

  return {
    artist_id: person.id,
    artist_name: getArtistName(person),
    week_start: weekStart,
    week_end: weekEnd,
    expected_minutes: expectedMinutes,
    expected_days: roundDays(expectedMinutes / thresholds.minutesPerDay),
    logged_minutes: loggedMinutes,
    logged_days: roundDays(loggedMinutes / thresholds.minutesPerDay),
    allocated_minutes: allocatedMinutes,
    allocated_days: roundDays(allocatedMinutes / thresholds.minutesPerDay),
    unaccounted_minutes: unaccountedMinutes,
    unaccounted_days: roundDays(unaccountedMinutes / thresholds.minutesPerDay),
    timelog_completeness_ratio: Math.round(clamp(timelogRatio, 0, 1.5) * 1000) / 1000,
    utilisation_ratio: Math.round(clamp(utilisationRatio, 0, 1.5) * 1000) / 1000,
    confidence_category: confidence,
    reason_codes: Array.from(reasonCodes),
    notes: buildNotes(status, Array.from(reasonCodes)),
    status,
    departments,
    task_count: assignedTasks.length,
    log_count: personLogs.length,
    used_schedule_assumptions: usedScheduleAssumptions,
    adjusted_time_candidate_minutes: loggedMinutes + recoveredMissingMinutes,
    adjusted_time_candidate_days: roundDays((loggedMinutes + recoveredMissingMinutes) / thresholds.minutesPerDay),
    recovered_missing_minutes: recoveredMissingMinutes || undefined,
    recovered_missing_days: recoveredMissingMinutes ? roundDays(recoveredMissingMinutes / thresholds.minutesPerDay) : undefined,
    finance_confidence_hint: confidence,
    task_summaries: taskSummaries,
    last_computed_at: context.now || new Date().toISOString(),
  };
}

function sumRows(rows: ArtistWeekReconciliationSummary[]): FinanceReconciliationPeriodSummary {
  const totals = rows.reduce(
    (acc, row) => {
      acc.expected += row.expected_minutes;
      acc.logged += row.logged_minutes;
      acc.allocated += row.allocated_minutes;
      acc.unaccounted += row.unaccounted_minutes;
      if (row.confidence_category === "low") acc.lowConfidence += 1;
      return acc;
    },
    { expected: 0, logged: 0, allocated: 0, unaccounted: 0, lowConfidence: 0 }
  );

  return {
    total_expected_minutes: totals.expected,
    total_expected_days: roundDays(totals.expected / DEFAULT_THRESHOLDS.minutesPerDay),
    total_logged_minutes: totals.logged,
    total_logged_days: roundDays(totals.logged / DEFAULT_THRESHOLDS.minutesPerDay),
    total_allocated_minutes: totals.allocated,
    total_allocated_days: roundDays(totals.allocated / DEFAULT_THRESHOLDS.minutesPerDay),
    total_unaccounted_minutes: totals.unaccounted,
    total_unaccounted_days: roundDays(totals.unaccounted / DEFAULT_THRESHOLDS.minutesPerDay),
    timelog_completeness_ratio: totals.expected > 0 ? Math.round(clamp(totals.logged / totals.expected, 0, 1.5) * 1000) / 1000 : 1,
    utilisation_ratio: totals.expected > 0 ? Math.round(clamp(totals.allocated / totals.expected, 0, 1.5) * 1000) / 1000 : 1,
    low_confidence_artist_weeks: totals.lowConfidence,
    artist_week_count: rows.length,
  };
}

function buildAlerts(rows: ArtistWeekReconciliationSummary[], summary: FinanceReconciliationPeriodSummary, thresholds: FinanceReconciliationThresholds) {
  const partialWeeks = rows.filter((row) => row.reason_codes.includes("partial_timelogs") || row.reason_codes.includes("missing_timelogs"));
  const underAllocatedWeeks = rows.filter((row) => row.reason_codes.includes("under_allocated"));
  const lowConfidenceWeeks = rows.filter((row) => row.confidence_category === "low");
  const alerts: FinanceReconciliationAlert[] = [];

  if (partialWeeks.length > 0) {
    alerts.push({
      id: "partial-timelogs",
      tone: partialWeeks.length > 5 ? "critical" : "warning",
      title: "Artist-weeks with partial timelogs",
      summary: `${partialWeeks.length} artist-weeks show missing or partial timelogs in the selected period.`,
    });
  }

  if (summary.total_unaccounted_days >= thresholds.problemUnaccountedDays) {
    alerts.push({
      id: "unaccounted-time",
      tone: summary.total_unaccounted_days >= thresholds.problemUnaccountedDays * 3 ? "critical" : "warning",
      title: "Unaccounted paid time",
      summary: `${summary.total_unaccounted_days} days of expected paid time were not logged to tasks in the selected period.`,
    });
  }

  if (underAllocatedWeeks.length > 0) {
    alerts.push({
      id: "under-allocation",
      tone: "info",
      title: "Under-allocation risk",
      summary: `${underAllocatedWeeks.length} artist-weeks look under-utilised because allocation fell short of expected working time.`,
    });
  }

  if (summary.artist_week_count > 0) {
    const lowConfidencePct = Math.round((summary.low_confidence_artist_weeks / summary.artist_week_count) * 100);
    if (lowConfidencePct > 0) {
      alerts.push({
        id: "low-confidence-period",
        tone: lowConfidencePct >= 30 ? "critical" : "warning",
        title: "Low-confidence labour data",
        summary: `${lowConfidencePct}% of artist-weeks in this period are low confidence.`,
      });
    }
  }

  return alerts;
}

export function buildFinanceReconciliationReport(args: BuildFinanceReconciliationReportArgs): FinanceReconciliationReport {
  const thresholds = { ...DEFAULT_THRESHOLDS, ...(args.thresholds || {}) };
  const weeks = listIsoWeeksBetween(args.startDate, args.endDate);
  const relevantPeople = args.people.filter((person) => {
    const hasLogs = (args.timeLogs || []).some(
      (timeLog) =>
        timeLog.personId === person.id &&
        timeLog.logDate >= args.startDate &&
        timeLog.logDate <= args.endDate
    );
    const hasAssignments = (args.tasks || []).some((task) => (task.assignedArtistIds || []).includes(person.id));
    return hasLogs || hasAssignments;
  });

  const rows = relevantPeople.flatMap((person) =>
    weeks.map((weekStart) =>
      computeArtistWeekReconciliationSummary(person, weekStart, {
        tasks: args.tasks,
        timeLogs: args.timeLogs,
        availabilityAdjustments: args.availabilityAdjustments,
        thresholds,
        now: args.now,
      })
    )
  );

  const summary = sumRows(rows);
  const alerts = buildAlerts(rows, summary, thresholds);

  return {
    rows,
    summary,
    alerts,
    thresholds,
    generatedAt: args.now || new Date().toISOString(),
  };
}

export const financeReconciliationDefaults = DEFAULT_THRESHOLDS;
